import { FC, useCallback, useMemo, useRef } from 'react';
import stashArrowsSwitch from 'src/redesign/assets/stashArrowsSwitch.svg';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import type { Site } from 'src/types/types';
import { OverlayPanel } from 'primereact/overlaypanel';
import { TextButton } from 'wix-style-react';
import { classes } from './Dropdown.st.css';
import { ChevronDown } from 'wix-ui-icons-common';
import c from 'clsx';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Divider } from 'primereact/divider';
import { updateSelectedSite } from 'src/redesign/features/Account/store/actions';
import { fetchEvents } from 'src/redesign/features/Events/store/actions';
import { allUserSitesSelector } from 'src/redesign/features/Sites/selectors';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';
import { fetchSiteDataById } from 'src/redesign/features/SelectedSite/store/actions';
import { useHistory } from 'react-router-dom';
import { globalReloadingAction, globalReloadingEndAction } from 'src/redesign/shared/store/reducer';

type TSiteDropdownProps = {
  onAddSiteClick: () => void;
};

export const SitesDropdown: FC<TSiteDropdownProps> = ({ onAddSiteClick }) => {
  const dispatch = useAppDispatch();
  const siteButtonRef = useRef<OverlayPanel>();
  const currentSite = useAppSelector(getSelectedSiteDataSelector);
  const allSites = useAppSelector(allUserSitesSelector);

  const handlePopupMenuOnClick = useCallback(
    (site: Site) => {
      dispatch(updateSelectedSite({ siteId: site.id }))
        .unwrap()
        .then(() => {
          // Redirect to the root URL instead of reloading the current page
          window.location.href = process.env.DASHBOARD_CLIENT_URL || '/';
        })
        .catch((err) => {
          console.error('Failed to update selected site', err);
        });

      siteButtonRef.current?.hide();
    },
    [dispatch],
  );
  const sitesModel: MenuItem[] = useMemo(
    () =>
      allSites?.map((site: Site): MenuItem => {
        return {
          id: site.id,
          label: site.site_display_name,
          data: site,
          command: () => {
            handlePopupMenuOnClick(site);
          },
        };
      }),
    [handlePopupMenuOnClick, allSites],
  );
  if (!allSites || !Array.isArray(allSites)) return null;

  return (
    <>
      <TextButton onClick={(e) => siteButtonRef?.current?.toggle(e)}>
        <div className={classes.buttonContent}>
          <img className={classes.arrow} src={stashArrowsSwitch} alt="arrows" />
          <span className={classes.siteName}>
            {currentSite?.site_display_name || 'Site loading...'}
          </span>
          <ChevronDown color="black" className={c(classes.chevron)} />
        </div>
      </TextButton>
      <OverlayPanel ref={siteButtonRef}>
        <span className={classes.menuHeader}>SWITCH TO:</span>
        <Menu
          pt={{
            root: {
              className: classes.rootMenu,
            },
            menu: {
              className: classes.menu,
            },
            menuitem: {
              className: classes.menuItem,
            },
            action: {
              className: classes.menuAction,
            },
          }}
          model={sitesModel}
        />
        <Divider pt={{ root: { className: classes.divider } }} />
        <TextButton onClick={onAddSiteClick} className={classes.addSiteButton}>
          + Add New Site
        </TextButton>
      </OverlayPanel>
    </>
  );
};
